import React from 'react';
import {
  Box,
  Button,
  Text,
  VStack,
  Icon,
  IconButton,
  HStack,
} from 'src/components/ui';
import { CloseIcon } from '@chakra-ui/icons';
import { useOnboarding } from 'src/contexts/onboarding';
import { FaRocket, FaPalette, FaBullseye, FaPlay } from 'react-icons/fa';
import {
  RIGHT_SIDEBAR_WIDTH_COLLAPSED,
  TOP_BAR_HEIGHT,
} from 'src/components/campaigns/utils/constants';

/**
 * WelcomeWizard Component
 *
 * Shows on first login for new users.
 * Modern, polished design with clear call-to-action.
 */
const WelcomeWizard: React.FC = () => {
  const {
    showWelcomeModal,
    closeWelcomeModal,
    setWizard1Skipped,
    triggerMenuTour,
  } = useOnboarding();

  const handleTakeTour = () => {
    closeWelcomeModal();
    triggerMenuTour();
  };

  const handleSkip = () => {
    setWizard1Skipped();
    closeWelcomeModal();
  };

  const features = [
    { icon: FaRocket, text: 'Launch campaigns in minutes', color: 'orange.500' },
    { icon: FaPalette, text: 'AI-powered creative design', color: 'purple.500' },
    { icon: FaBullseye, text: 'Smart audience targeting', color: 'blue.500' },
  ];

  if (!showWelcomeModal) return null;

  return (
    <>
      {/* Backdrop - starts below TopBar, stops before right sidebar to avoid double overlay */}
      <Box
        position="fixed"
        top={TOP_BAR_HEIGHT}
        left={0}
        right={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
        bottom={0}
        bg="blackAlpha.600"
        backdropFilter="blur(4px)"
        zIndex={10000}
        onClick={handleSkip}
      />

      {/* Dark overlay for TopBar - same style as rest of overlay */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
        h={TOP_BAR_HEIGHT}
        zIndex={10202}
        bg="blackAlpha.600"
        backdropFilter="blur(4px)"
        pointerEvents="none"
      />

      {/* Dark overlay for Right Sidebar - covers entire sidebar height */}
      <Box
        position="fixed"
        top={0}
        right={0}
        bottom={0}
        w={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
        zIndex={10202}
        bg="blackAlpha.600"
        backdropFilter="blur(4px)"
        pointerEvents="none"
      />

      {/* Modal */}
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        zIndex={10001}
        w="90%"
        maxW="440px"
        bg="white"
        borderRadius="24px"
        boxShadow="0 25px 60px -15px rgba(0, 0, 0, 0.3)"
        overflow="hidden"
        sx={{
          animation: 'modalEnter 0.3s ease-out',
          '@keyframes modalEnter': {
            '0%': {
              opacity: 0,
              transform: 'translate(-50%, -48%) scale(0.95)',
            },
            '100%': {
              opacity: 1,
              transform: 'translate(-50%, -50%) scale(1)',
            },
          },
        }}
      >
        {/* Header with gradient and decorative elements */}
        <Box
          bg="linear-gradient(135deg, #F6851E 0%, #FF9F4A 50%, #F6851E 100%)"
          pt={10}
          pb={12}
          px={8}
          position="relative"
          overflow="hidden"
        >
          {/* Decorative circles */}
          <Box
            position="absolute"
            top="-40px"
            right="-40px"
            w="120px"
            h="120px"
            borderRadius="full"
            bg="rgba(255,255,255,0.1)"
          />
          <Box
            position="absolute"
            bottom="-50px"
            left="-30px"
            w="140px"
            h="140px"
            borderRadius="full"
            bg="rgba(255,255,255,0.08)"
          />
          <Box
            position="absolute"
            top="20px"
            left="30%"
            w="60px"
            h="60px"
            borderRadius="full"
            bg="rgba(255,255,255,0.05)"
          />

          {/* Close button */}
          <IconButton
            aria-label="Close"
            icon={<CloseIcon boxSize={3} />}
            position="absolute"
            top={4}
            right={4}
            size="sm"
            variant="ghost"
            color="white"
            _hover={{ bg: 'rgba(255,255,255,0.2)' }}
            onClick={handleSkip}
            zIndex={1}
          />

          {/* Logo/Icon */}
          <VStack spacing={4} position="relative" zIndex={1}>
            <VStack spacing={1}>
              <Text fontSize="2xl" fontWeight="bold" color="white" letterSpacing="-0.5px">
                Welcome to FusionAds.ai
              </Text>
              <Text fontSize="sm" color="whiteAlpha.900" fontWeight="500">
                Your AI-powered advertising platform
              </Text>
            </VStack>
          </VStack>
        </Box>

        {/* Content */}
        <VStack spacing={6} py={8} px={8}>
          {/* Features */}
          <VStack spacing={4} w="full" align="stretch">
            {features.map((feature, index) => (
              <HStack
                key={index}
                spacing={4}
                p={3}
                borderRadius="12px"
                transition="all 0.2s"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  w="40px"
                  h="40px"
                  borderRadius="10px"
                  bg="white"
                  boxShadow="0 2px 8px rgba(0, 0, 0, 0.06)"
                >
                  <Icon as={feature.icon} color={feature.color} boxSize={5} />
                </Box>
                <Text fontSize="sm" color="gray.700" fontWeight="600">
                  {feature.text}
                </Text>
              </HStack>
            ))}
          </VStack>

          {/* Buttons */}
          <VStack spacing={3} w="full" pt={2}>
            <Button
              colorScheme="orange"
              size="lg"
              w="full"
              h="52px"
              fontSize="md"
              fontWeight="600"
              borderRadius="12px"
              onClick={handleTakeTour}
              leftIcon={<Icon as={FaPlay} boxSize={3} />}
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(246, 133, 30, 0.4)',
              }}
              _active={{
                transform: 'translateY(0)',
              }}
              transition="all 0.2s"
            >
              Take the Tour
            </Button>

            <Button
              variant="ghost"
              color="gray.500"
              size="md"
              onClick={handleSkip}
              fontWeight="500"
              _hover={{
                color: 'gray.700',
                bg: 'transparent',
              }}
            >
              Skip for now
            </Button>
          </VStack>
        </VStack>
      </Box>
    </>
  );
};

export default WelcomeWizard;
